import React, { forwardRef, useCallback, useImperativeHandle, useState } from 'react';

import { AddCreditCardFormInputs, PaymentTotals } from 'components/payments/components';
import { useAddCreditCardFormInputs } from 'components/payments/components/add-credit-card-form-inputs/hooks/use-add-credit-card-form-inputs';
import { PaymentMethodDropdown } from 'components/payments/components/payment-method-dropdown/payment-method-dropdown';
import { addPaymentMethodDetails } from 'components/payments/components/payment-method-option/constants';
import {
  PaymentMethod,
  PaymentMethodOptionTypes,
} from 'components/payments/integrations/hooks/types';
import useApplePay from 'components/payments/integrations/hooks/use-apple-pay';
import useGooglePay from 'components/payments/integrations/hooks/use-google-pay';
import { usePaymentMethodsAdapter } from 'components/payments/integrations/hooks/use-payment-methods-adapter';
import { isNewCreditCard } from 'components/payments/integrations/hooks/utils';
import {
  IEncryptionError,
  IEncryptionResult,
} from 'components/payments/integrations/orbital/components/encryption/types';
import { useOrbitalVaultedPayment } from 'components/payments/integrations/orbital/hooks/use-orbital-vaulted-payment';
import useEffectOnce from 'hooks/use-effect-once';
import {
  IOrderInformation,
  IPaymentCollectorProps,
  IPaymentCollectorRef,
  IPaymentMethodDetails,
  IPlaceOrderWithNewCreditCard,
  IPlaceOrderWithVaultedCard,
  PlaceOrderResult,
} from 'pages/cart/payments/types';
import { useCartContext } from 'state/cart';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import {
  PaymentFieldVariations,
  defaultPaymentFieldVariation,
} from 'state/launchdarkly/variations';
import logger from 'utils/logger';
import { isDelivery } from 'utils/service-mode';

import { SafetechEncryptionInteractive } from './components/encryption';
import { useOrbitalAddPaymentMethod } from './components/orbital-add-credit-card-modal/hooks/use-orbital-add-payment-method';
import { buildMutationInput as buildAddPaymentMethodMutationInput } from './components/orbital-add-credit-card-modal/utils';
import { useOrbitalOneTimePayment } from './hooks/use-orbital-one-time-payment';

export const OrbitalPaymentCollector = forwardRef<IPaymentCollectorRef>(
  ({ hidePaymentMethod, isProcessingPlaceOrder = false }: IPaymentCollectorProps, ref) => {
    const paymentFieldVariations =
      useFlag<PaymentFieldVariations>(LaunchDarklyFlag.PAYMENT_FIELD_VARIATIONS) ||
      defaultPaymentFieldVariation;
    const migrationEnabled = useFlag(LaunchDarklyFlag.ENABLE_DIGITAL_WALLET_MIGRATION);

    const oneTimePaymentFlowEnabled = useFlag(
      LaunchDarklyFlag.ENABLE_VAULT_AFTER_MAKING_ONE_TIME_PAYMENT
    );

    const { serverOrder, serviceMode } = useCartContext();
    const { feCountryCode: billingCountry } = useLocale();

    const [encryptionResult, setEncryptionResult] = useState<IEncryptionResult | undefined>(
      undefined
    );
    const [selectedPaymentMethod, setSelectedPaymentMethod] =
      useState<PaymentMethod>(addPaymentMethodDetails);

    const { requestApplePayPayment } = useApplePay({
      prepaid: false,
    });
    const { requestGooglePayPayment } = useGooglePay(serverOrder?.rbiOrderId || '');
    const { addPaymentMethod } = useOrbitalAddPaymentMethod();
    const { requestOneTimePayment } = useOrbitalOneTimePayment(serverOrder?.rbiOrderId || '');
    const { requestVaultedPayment } = useOrbitalVaultedPayment(serverOrder?.rbiOrderId || '');

    // Get the user's available payment methods
    const { availablePaymentMethods, defaultPaymentMethod, loading } = usePaymentMethodsAdapter();
    // Set the user's default payment method as the selected option.
    // If not, set the next available payment method option.
    // If not, set the generic option so a user can add a payment method.
    useEffectOnce(() => {
      let paymentMethod: PaymentMethod = addPaymentMethodDetails;
      if (defaultPaymentMethod) {
        paymentMethod = defaultPaymentMethod;
      } else if (availablePaymentMethods.length) {
        paymentMethod = availablePaymentMethods[0];
      }
      setSelectedPaymentMethod(paymentMethod);
    });

    const handlePaymentMethodSelection = (method: PaymentMethod) => {
      setSelectedPaymentMethod(method);
    };

    const isDeliveryOrder = isDelivery(serviceMode);
    const isUsingNewCreditCard = isNewCreditCard(selectedPaymentMethod);

    const showAddCreditCardForm =
      selectedPaymentMethod.id === PaymentMethodOptionTypes.ADD_CREDIT_CARD;

    const {
      creditCardFormErrors,
      creditCardFormValues,
      handleCreditCardFormChanges,
      validateCreditCardFormOnSubmit,
    } = useAddCreditCardFormInputs({
      billingCountry,
      paymentFieldVariations,
    });

    // Handle encryption result and fire off add payment method request
    const onEncryptionResult = useCallback((encryptionData: IEncryptionResult) => {
      setEncryptionResult(encryptionData);
    }, []);

    // Handle encryption error
    const onEncryptionError = useCallback((_error: IEncryptionError) => {
      setEncryptionResult(undefined);
    }, []);

    /**
     * This is used with paymentCollectorRef in the checkout page (payment.tsx). It is a unique case in which we need to be able
     * to call a method within a child to send the current payment details for a user when, and only when, it is needed.
     */
    useImperativeHandle(
      ref,
      () => {
        return {
          // TODO: Payments Refactor - include further logic to pass payment details to parent when requested.
          getPaymentMethodDetails: (): IPaymentMethodDetails => ({
            paymentMethod: selectedPaymentMethod,
            ...(isUsingNewCreditCard && {
              creditCardFormValues,
              encryptionResult,
            }),
          }),
          placeOrderWithGooglePay: async (
            orderInformation: IOrderInformation
          ): PlaceOrderResult => {
            const { total, storeEmail, rbiOrderId } = orderInformation;
            return await requestGooglePayPayment({
              total,
              billingCountry,
              migrationEnabled,
              storeEmail,
              rbiOrderId,
              orderInformation,
            });
          },
          placeOrderWithApplePay: (orderInformation: IOrderInformation): PlaceOrderResult => {
            return requestApplePayPayment({
              billingCountry,
              orderInformation,
            });
          },
          placeOrderWithVaultedCard: async ({
            orderInformation,
            paymentMethod,
          }: IPlaceOrderWithVaultedCard): PlaceOrderResult => {
            return requestVaultedPayment({
              accountIdentifier: paymentMethod.id,
              cardBrand: paymentMethod.brand,
              orderInformation,
            });
          },
          placeOrderWithNewCreditCard: async ({
            creditCardFormValues,
            encryptionResult,
            orderInformation,
          }: IPlaceOrderWithNewCreditCard): PlaceOrderResult => {
            const isFormValid = validateCreditCardFormOnSubmit();
            if (!isFormValid) {
              return undefined;
            }
            if (encryptionResult === null) {
              logger.error({
                message: 'Orbital card encryption failed',
              });
              return undefined;
            }
            if (creditCardFormValues.saveCard && !oneTimePaymentFlowEnabled) {
              const addPaymentMethodResponse = await addPaymentMethod({
                input: buildAddPaymentMethodMutationInput(
                  creditCardFormValues,
                  encryptionResult as IEncryptionResult,
                  paymentFieldVariations,
                  billingCountry
                ),
                options: {
                  skipErrorDialogOnError: true,
                },
              });
              return requestVaultedPayment({
                accountIdentifier: addPaymentMethodResponse.id,
                cardBrand: addPaymentMethodResponse.brand,
                orderInformation,
              });
            }
            return requestOneTimePayment({
              creditCardFormValues,
              encryptionData: encryptionResult as IEncryptionResult,
              orderInformation,
            });
          },
          placeOrderWithGiftCard: async () => {
            return undefined;
          },
        };
      },
      [
        selectedPaymentMethod,
        isUsingNewCreditCard,
        creditCardFormValues,
        encryptionResult,
        requestGooglePayPayment,
        billingCountry,
        migrationEnabled,
        requestApplePayPayment,
        requestVaultedPayment,
        validateCreditCardFormOnSubmit,
        oneTimePaymentFlowEnabled,
        requestOneTimePayment,
        addPaymentMethod,
        paymentFieldVariations,
      ]
    );

    /**
     * If you need to only access the payment operations you will be able
     * to hide the payment methods selector.
     */
    if (hidePaymentMethod) {
      return null;
    }

    return (
      <>
        <PaymentMethodDropdown
          aria-expanded="true"
          handleSelection={handlePaymentMethodSelection}
          loading={loading}
          paymentMethods={availablePaymentMethods}
          selectedPaymentMethod={selectedPaymentMethod}
        />
        {showAddCreditCardForm && (
          <>
            <AddCreditCardFormInputs
              errors={creditCardFormErrors}
              values={creditCardFormValues}
              isDelivery={isDeliveryOrder}
              onChange={handleCreditCardFormChanges}
              showSaveCard
            />
            <SafetechEncryptionInteractive
              cardNumber={creditCardFormValues.cardNumber}
              cvv={creditCardFormValues.cvv || ''}
              onResult={onEncryptionResult}
              onError={onEncryptionError}
            />
          </>
        )}
        <PaymentTotals tipSelectorDisabled={isProcessingPlaceOrder} />
      </>
    );
  }
);
